// src/components/portal/FunderLogin.js
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getAuth, signInWithCustomToken, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { getFunctions } from "firebase/functions";
import { httpsCallable } from "../../shared/legacyData/legacyCallable";
import tpcLogo from "../../images/TPC-logo-light.png";
import "./Portalauth.css";

const CONFIG = {
company: "TPC_Group",
collectionBrand: "TechPerformanceCentre",
registerRoute: "/funder-register",
dashboardRoute: "/funder-dashboard",
roleSelectRoute: "/portal-role-select",
};

const EMAIL_ONLY_FUNDER_EXCEPTIONS = {
"devania@tpc.training": "hannes.van.deventer@epiroc.com",
};

export default function FunderLogin() {
const [email, setEmail]       = useState("");
const [password, setPassword] = useState("");
const [busy, setBusy]         = useState(false);
const [err, setErr]           = useState("");

const nav = useNavigate();
const fn  = getFunctions();
const getSystemDocs     = httpsCallable(fn, "getSystemDocuments");

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
const funderEmail = (doc) => normalizeEmail(doc?.ContactOfficerEmail || doc?.contactOfficerEmail || doc?.contactEmail || doc?.email);
const getLoginLookupEmail = (value) => normalizeEmail(EMAIL_ONLY_FUNDER_EXCEPTIONS[normalizeEmail(value)] || value);
const isEmailOnlyException = (value) => Boolean(EMAIL_ONLY_FUNDER_EXCEPTIONS[normalizeEmail(value)]);

const pickFunderDoc = (docs, { email: targetEmail, authUid }) => {
const lookupEmail = getLoginLookupEmail(targetEmail);
const rows = Array.isArray(docs) ? docs : [];
return rows.find((doc) => {
    const portalUsers = Array.isArray(doc?.portalUsers) ? doc.portalUsers : [];
    return portalUsers.some((user) => user?.authUid === authUid && normalizeEmail(user?.email) === targetEmail);
    })
    || rows.find((doc) => doc?.authUid && doc.authUid === authUid && funderEmail(doc) === targetEmail)
    || rows.find((doc) => isEmailOnlyException(targetEmail) && funderEmail(doc) === lookupEmail)
    || rows.find((doc) => authUid && funderEmail(doc) === targetEmail)
    || rows.find((doc) => {
    const portalUsers = Array.isArray(doc?.portalUsers) ? doc.portalUsers : [];
    return portalUsers.some((user) => user?.authUid === authUid);
    })
    || rows.find((doc) => doc?.authUid && doc.authUid === authUid)
    || null;
};

const rowsWithDocumentIds = (data) => {
if (Array.isArray(data)) return data;
return Object.entries(data || {}).map(([docId, value]) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return value;
    return {
    ...value,
    id: value.id || value.uniqueId || docId,
    uniqueId: value.uniqueId || value.id || docId,
    docId: value.docId || docId,
    };
});
};

const handleLogin = async (e) => {
e.preventDefault();
setErr("");
if (!email) return setErr("Please enter your email address.");
setBusy(true);

try {
    const safeEmail = normalizeEmail(email);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeEmail)) {
    setBusy(false);
    return setErr("Please enter a valid email address.");
    }

    const emailOnlyException = isEmailOnlyException(safeEmail);
    if (!emailOnlyException && !password) {
    setBusy(false);
    return setErr("Please enter your password.");
    }

    const portalAuth = getAuth();
    let firebaseUser = null;
    if (emailOnlyException) {
    const emailOnlyLogin = httpsCallable(fn, "funderEmailOnlySession");
    const tokenResult = await emailOnlyLogin({ email: safeEmail });
    const customToken = tokenResult?.data?.customToken;
    if (!customToken) throw new Error("Email-only funder login did not return a session token.");
    const credential = await signInWithCustomToken(portalAuth, customToken);
    firebaseUser = credential.user;
    } else {
    const credential = await signInWithEmailAndPassword(portalAuth, safeEmail, password);
    firebaseUser = credential.user;
    if (!firebaseUser.emailVerified) {
        await signOut(portalAuth);
        setBusy(false);
        return setErr("Please verify your email address before logging in.");
    }
    }

    // The allowlisted internal login must not depend on a pre-login funder
    // collection read. The dashboard resolves this alias to the live funder
    // record after the limited email-only session has been created.
    let funderDoc = emailOnlyException
    ? { ContactOfficerEmail: getLoginLookupEmail(safeEmail) }
    : null;

    if (!emailOnlyException) {
    const funderCollections = await Promise.all(["funders", "Funders"].map(async (systemName) => {
        try {
        const res = await getSystemDocs({
            company: CONFIG.company,
            collectionBrand: CONFIG.collectionBrand,
            systemName,
            auth: true,
        });
        const payload = res?.data?.data ?? res?.data?.documents ?? res?.data;
        return rowsWithDocumentIds(payload);
        } catch (_) {
        return [];
        }
    }));

    funderDoc = pickFunderDoc(funderCollections.flat(), { email: safeEmail, authUid: firebaseUser?.uid || "" });
    }

    if (!funderDoc) {
    if (firebaseUser) await signOut(portalAuth);
    setBusy(false);
    return setErr("No registered funder account found for this login. Please use your invite link or contact TPC administration.");
    }

    // 3. Check if also a learner (Applicant) — edge case
    let isAlsoLearner = false;
    try {
    const applicantRes = await getSystemDocs({
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: "Applicants",
        filters: [{ field: "email", operator: "==", value: safeEmail }],
        auth: true,
    }); 
    const applicants = Array.isArray(applicantRes.data)
        ? applicantRes.data
        : (applicantRes.data?.documents || []);
    isAlsoLearner = applicants.length > 0 && applicants[0]?.isCompleted;
    } catch (_) { /* ignore */ }

    // 4. Store session
    sessionStorage.setItem("portalRole", "funder");
    sessionStorage.setItem("portalEmail", safeEmail);
    sessionStorage.setItem("portalFunderId", funderDoc.id || funderDoc.uniqueId || funderDoc.docId || "");
    sessionStorage.removeItem("portalFunderNameHint");
    sessionStorage.setItem("portalFunderDoc", JSON.stringify(funderDoc));
    sessionStorage.setItem("portalIsAlsoLearner", isAlsoLearner ? "true" : "false");
    sessionStorage.setItem("portalUser", JSON.stringify({
    role: "funder",
    email: safeEmail,
    name: funderDoc.ContactOfficerName || funderDoc.contactOfficerName || funderDoc.FunderName || funderDoc.funderName || "",
    id: funderDoc.id || funderDoc.uniqueId || funderDoc.docId || "",
    authUid: firebaseUser?.uid || "",
    emailOnlyException,
    funder: funderDoc,
    }));

    // 5. Route
    if (isAlsoLearner) {
    nav(CONFIG.roleSelectRoute);
    } else {
    nav(CONFIG.dashboardRoute);
    }
} catch (ex) {
    console.error("[FunderLogin] error:", ex);
    if (ex.code === "auth/invalid-credential" || ex.code === "auth/wrong-password" || ex.code === "auth/user-not-found") {
    setErr("Incorrect email or password.");
    } else if (ex.code === "auth/invalid-email") {
    setErr("Please enter a valid email address.");
    } else if (ex.code === "auth/too-many-requests") {
    setErr("Too many failed login attempts. Please try again later.");
    } else {
    setErr("Unable to verify your account. Please try again or contact TPC support.");
    }
} finally {
    setBusy(false);
}
};

return (
<div className="ar-container">
    {/* ── Left dark panel ── */}
    <div className="ar-left">
    <div className="ar-logo-box">
        <img src={tpcLogo} alt="TPC" className="ar-logo" />
    </div>
    <div className="ar-banner-text">Welcome Back, Funder</div>
    </div>

    {/* ── Right white panel ── */}
    <div className="ar-right">
    <form className="ar-form" onSubmit={handleLogin}>
        <h2 className="ar-title">Funder Dashboard Login</h2>

        {err && <p className="ar-err">{err}</p>}
        <input
        type="email"
        placeholder="Email"
        className="ar-input"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        />

        <input
        type="password"
        placeholder="Password"
        className="ar-input"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        />

        <button type="submit" className="ar-button" disabled={busy}>
        {busy ? (
            <div className="ar-button-loader">
            <div className="ar-spinner" />
            <span>Signing in…</span>
            </div>
        ) : "LOGIN"}
        </button>

        <p className="ar-small">
        Not registered?&nbsp;
        <Link to={CONFIG.registerRoute} style={{ fontWeight:"700" }}>Register here</Link>
        </p>

        <p className="ar-small" style={{ marginTop:"8px" }}>
        <Link to="/" style={{ color:"#9ca3af" }}>← Back to andus.co.za</Link>
        </p>
    </form>
    </div>
</div>
);
}
